import {
  OrganizationType,
  PositionType,
  type PublicOpenSearchResponse,
  RecruitmentStatus,
} from '@on3/api';
import { On3IconDownArrow } from '@on3/icons';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { classYearClaimCutoff } from '@on3/ui-lib/utils/constants';
import { VerifiedBadge } from '@public/components/Elite/VerifiedBadge/VerifiedBadge';
import { StarRating } from '@public/components/UI/StarRating/StarRating';
import clsx from 'clsx';
import { useMemo } from 'react';

import styles from './AdvancedSearchItem.module.scss';

interface AdvancedSearchItemProps {
  person: PublicOpenSearchResponse;
}
export const AdvancedSearchItem = ({ person }: AdvancedSearchItemProps) => {
  const {
    athleteVerified,
    classYear,
    currentOrganization,
    defaultAsset,
    division,
    formattedHeight,
    homeTownName,
    name,
    highSchool,
    positionTypes,
    organizationLevel,
    position,
    positionAbbreviation,
    predictions,
    college,
    rating,
    slug,
    sport,
    status,
    tier,
    weight,
  } = person;

  const { committedOrganization } = status || {};
  const { fiveStarPlus, consensusRating, consensusStars } = rating || {};

  const isPro = organizationLevel === OrganizationType.Professional;
  const isCoach = position?.positionType === PositionType.Coach;
  const isCoachAndPlayer =
    positionTypes?.includes(PositionType.Coach) &&
    positionTypes?.includes(PositionType.Player);

  const statusAsset =
    isPro || isCoach ? currentOrganization : committedOrganization;

  const isHighSchool = organizationLevel === OrganizationType.HighSchool;
  const showStatus =
    status?.type === RecruitmentStatus.Committed ||
    status?.type === RecruitmentStatus.Enrolled ||
    status?.type === RecruitmentStatus.Signed ||
    isPro;

  const displayPosition = isCoachAndPlayer
    ? rating?.positionAbbr
    : positionAbbreviation;
  const recruitHref = (slug && `/rivals/${slug}/industry-comparison/`) || '';
  const profileLink =
    isCoach && !isCoachAndPlayer
      ? `/rivals/coach/${slug}/`
      : `/rivals/${slug}/`;

  const committedOrgAsset = useMemo(() => {
    if (isCoachAndPlayer) {
      return { name: college?.name, ...college?.defaultAsset };
    }

    return { name: statusAsset?.name, ...statusAsset?.asset };
  }, [
    college?.defaultAsset,
    college?.name,
    isCoachAndPlayer,
    statusAsset?.asset,
    statusAsset?.name,
  ]);

  const renderClaimStatus = () => {
    if ((classYear || 0) < classYearClaimCutoff || isPro || isCoach) {
      return null;
    }

    if (athleteVerified) {
      return (
        <div className={styles.claimStatus}>
          <VerifiedBadge tier={tier} />
        </div>
      );
    }

    return (
      <div className={styles.claimStatus}>
        <Link
          className={styles.claim}
          forceReload
          href={`${profileLink}claim-profile/`}
        >
          Claim Profile <On3IconDownArrow />
        </Link>
      </div>
    );
  };

  return (
    <li className={styles.block} data-organization-level={organizationLevel}>
      <figure className={styles.headshot}>
        <Avatar
          altText={name}
          domain={defaultAsset?.domain}
          height={60}
          quality={95}
          source={defaultAsset?.source}
          width={60}
        />
      </figure>
      <div className={styles.info}>
        <Link href={profileLink}>{name}</Link>

        <div className={styles.details}>
          {isHighSchool && classYear ? (
            <dl data-name="classyear">
              <dt className="sr-only">Class Year</dt>
              <dd>{classYear}</dd>
              <dt className="sr-only">Division</dt>
              <dd aria-label="high school">H.S.</dd>
            </dl>
          ) : division ? (
            <dl data-name="division">
              <dt className="sr-only">Division</dt>
              <dd>{division}</dd>
            </dl>
          ) : null}
          {!!formattedHeight && formattedHeight.length > 2 && (
            <dl data-name="height">
              <dt className="sr-only">Height</dt>
              <dd>{formattedHeight}</dd>
            </dl>
          )}
          {!!weight && (
            <dl data-name="weight">
              <dt className="sr-only">Weight</dt>
              <dd>{weight}</dd>
            </dl>
          )}
        </div>
        {!!highSchool && !!homeTownName && (
          <div className={styles.homeTown}>
            {!!highSchool && (
              <dl data-name="highschool">
                <dt className="sr-only">High School</dt>
                <dd
                  title={
                    highSchool?.knownAs ?? highSchool?.name ?? 'high school'
                  }
                >
                  {highSchool?.knownAs ?? highSchool?.name ?? '-'}
                </dd>
              </dl>
            )}
            {!!homeTownName && (
              <dl data-name="hometown">
                <dt className="sr-only">Hometown</dt>
                <dd>{homeTownName}</dd>
              </dl>
            )}
          </div>
        )}
      </div>
      {!isPro && !isCoach && (
        <StarRating
          className={styles.stars}
          fiveStarPlus={fiveStarPlus}
          hasBorder={false}
          href={recruitHref}
          isBoldRating={false}
          isConsensus
          rating={consensusRating}
          sport={sport?.name}
          stars={consensusStars}
          year={String(classYear)}
        />
      )}
      {isCoachAndPlayer && (
        <Link
          className={styles.coachingProfileLink}
          href={`/rivals/coach/${slug}/`}
        >
          Coach Profile
        </Link>
      )}
      {renderClaimStatus()}
      {displayPosition && (
        <dl
          className={clsx(styles.position, { [styles.noBorder]: !showStatus })}
        >
          <dt className="sr-only">Position</dt>
          <dd>{displayPosition}</dd>
        </dl>
      )}
      {(showStatus || !!predictions?.length) && (
        <div
          className={clsx(styles.status, {
            [styles.noPosition]: !displayPosition,
          })}
          data-status={status?.type}
        >
          {showStatus && (
            <div className={styles.committedOrg}>
              <figure>
                <Avatar
                  altText={committedOrgAsset?.name}
                  domain={committedOrgAsset?.domain}
                  height={20}
                  source={committedOrgAsset?.source}
                  teamAvatar
                  title={committedOrgAsset?.name}
                  type={committedOrgAsset?.type}
                  width={20}
                />
              </figure>
              {status?.type === RecruitmentStatus.Committed &&
                !isPro &&
                !isCoach && <div className={styles.statusType}>commit</div>}
            </div>
          )}
          {!showStatus && !!predictions?.length && (
            <div className={styles.predictions}>
              {predictions?.map((p) => (
                <div key={p.key}>
                  <figure>
                    <Avatar
                      altText={p.organization?.name}
                      domain={p.organization?.asset?.domain}
                      height={20}
                      source={p.organization?.asset?.source}
                      teamAvatar
                      title={p.organization?.fullName}
                      type={p.organization?.asset?.type}
                      width={20}
                    />
                  </figure>
                  <span>{p.percent}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
};
